/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  AiOutlineBell,
  AiOutlineCloseCircle,
  AiOutlineCheck,
  AiOutlineDollar,
  AiOutlineBook,
  AiOutlineCreditCard,
  AiOutlineHome,
  AiOutlineDelete,
  AiOutlineSetting,
} from "react-icons/ai";
import { ThreeDots } from "react-loader-spinner";
import { useHistory } from "react-router-dom";
import { HiOutlineDocumentText, HiReceiptRefund } from "react-icons/hi";
import { BiBriefcase } from "react-icons/bi";
import { MdOutlineHandshake } from "react-icons/md";
import { toast } from "react-toastify";
import { getallNotificationsNew } from "../Redux/actions/NotificationNew";
import { NotificationSeenApi } from "../Api/Settings/Notification/Notification";
import { storage } from "../Utils/storageService";

const NotificationDrawer = ({ onClose, isOpen }) => {
  const dispatch = useDispatch();
  const primaryColor = "#0b6462";
  const primaryDark = "#084d4b";
  const primaryTint = "#e6f3f2";
  const primarySoft = "#d9eeed";

  const notifications = useSelector((state) => state.getnotificationsNew || {});

  const [accumulatedNotifications, setAccumulatedNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markAllLoading, setMarkAllLoading] = useState(false);

  const freshNotifications = notifications.data?.data || [];
  const pagination = notifications.data?.pagination || {};
  const totalPages = pagination.totalPages || 1;
  const unreadCount = pagination.unreadCount || 0;

  const notificationLoading = useSelector((state) => state.notifications?.loading || false);

  const [notificationRow, setNotificationRow] = useState(null);

  const getUnreadNotificationIds = () => {
    return accumulatedNotifications
      .filter((noti) => !noti.IsMarked)
      .map((noti) => noti.NotificationId);
  };

  const handleMarkAllAsRead = async () => {
    const unreadIds = getUnreadNotificationIds();

    if (unreadIds.length === 0) {
      toast.info("No unread notifications to mark");
      return;
    }

    setMarkAllLoading(true);
    const loginId = storage.getLoginId() || "";

    if (!loginId) {
      toast.error("Login ID not found. Please login again.");
      setMarkAllLoading(false);
      return;
    }

    try {
      const response = await NotificationSeenApi({ loginId, unreadIds });
      const responseData = response.data?.data || response.data;

      if (responseData?.IsSuccess === true) {
        setAccumulatedNotifications((prevNotifications) =>
          prevNotifications.map((noti) => ({
            ...noti,
            IsMarked: true,
          })),
        );

        if (notificationRow && !notificationRow.IsMarked) {
          setNotificationRow((prev) => (prev ? { ...prev, IsMarked: true } : null));
        }

        dispatch(getallNotificationsNew(1, pageSize));
      } else {
        console.error(responseData?.Message || "Failed to mark all as read");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.data?.Message || err.response?.data?.Message || "Something went wrong";

      console.error("Mark all read error:", errorMessage);
    } finally {
      setMarkAllLoading(false);
    }
  };

  useEffect(() => {
    if (freshNotifications.length > 0) {
      setAccumulatedNotifications((prev) => {
        return mergeNotifications(prev, freshNotifications);
      });
      setLoadingMore(false);
    }
  }, [freshNotifications]);

  const getNotificationIcon = (referenceType, referenceSubType) => {
    const isDelete = referenceSubType?.toLowerCase() === "delete";
    const iconColor = isDelete ? "#b91c1c" : primaryColor;
    const size = 22;

    if (!referenceType) {
      return <AiOutlineBell size={size} color={iconColor} />;
    }

    const type = referenceType.toLowerCase();

    // Payment types
    if (type.includes("payment")) {
      return <AiOutlineDollar size={size} color={iconColor} />;
    }

    // Receipt types
    if (type.includes("receipt")) {
      return <HiReceiptRefund size={size} color={iconColor} />;
    }

    // Journal voucher
    if (type.includes("journal")) {
      return <AiOutlineBook size={size} color={iconColor} />;
    }

    // Expense voucher
    if (type.includes("expense")) {
      return <AiOutlineCreditCard size={size} color={iconColor} />;
    }

    // Commission voucher
    if (type.includes("commission")) {
      return <AiOutlineCreditCard size={size} color={iconColor} />;
    }

    // Loan voucher
    if (type.includes("loan")) {
      return <MdOutlineHandshake size={size} color={iconColor} />;
    }

    // Land voucher
    if (type.includes("land")) {
      return <AiOutlineHome size={size} color={iconColor} />;
    }

    // Salary voucher
    if (type.includes("salary")) {
      return <BiBriefcase size={size} color={iconColor} />;
    }

    // Adjustment voucher
    if (type.includes("adjustment")) {
      return <AiOutlineSetting size={size} color={iconColor} />;
    }

    // Contract
    if (type.includes("contract")) {
      return <HiOutlineDocumentText size={size} color={iconColor} />;
    }

    // Reserved voucher (refund)
    if (type.includes("reserved")) {
      return <AiOutlineDelete size={size} color={iconColor} />;
    }

    // Default to bell
    return <AiOutlineBell size={size} color={iconColor} />;
  };

  const mergeNotifications = (existing, newItems) => {
    const notificationMap = new Map();

    existing.forEach((noti) => {
      notificationMap.set(noti.NotificationId, noti);
    });

    newItems.forEach((noti) => {
      notificationMap.set(noti.NotificationId, noti);
    });

    return Array.from(notificationMap.values()).sort(
      (a, b) => new Date(b.AddedOn || b.AddedDate) - new Date(a.AddedOn || a.AddedDate),
    );
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setAccumulatedNotifications([]);
      dispatch(getallNotificationsNew(1, pageSize));
    }
  }, [isOpen, dispatch, pageSize]);

  const loadMoreNotifications = () => {
    if (currentPage < totalPages && !loadingMore) {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      dispatch(getallNotificationsNew(nextPage, pageSize));
    }
  };
  const getIconBackground = (referenceSubType) => {
    if (referenceSubType?.toLowerCase() === "delete") {
      return "linear-gradient(135deg, #fef2f2, #fee2e2)";
    }
    return `linear-gradient(135deg, ${primaryTint}, ${primarySoft})`;
  };

  const handleMarkRead = async (notificationId) => {
    const loginId = storage.getLoginId() || "";

    if (!loginId) {
      toast.error("Login ID not found. Please login again.");

      return;
    }

    try {
      const response = await NotificationSeenApi({
        loginId,
        unreadIds: [notificationId],
      });
      const responseData = response.data?.data || response.data;

      if (responseData?.IsSuccess === true) {
        setAccumulatedNotifications((prevNotifications) =>
          prevNotifications.map((noti) =>
            noti.NotificationId === notificationId ? { ...noti, IsMarked: true } : noti,
          ),
        );

        if (notificationRow?.NotificationId === notificationId) {
          setNotificationRow((prev) => (prev ? { ...prev, IsMarked: true } : null));
        }

        dispatch(getallNotificationsNew(1, pageSize));
      } else {
      }
    } catch (err) {
      console.error("Mark read error:", err);
    }
  };

  const formatTimeDisplay = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    const diff = Math.floor((now - date) / 60000);

    if (diff <= 0) {
      return "Just now";
    }

    if (diff < 60) {
      return `${diff}m ago`;
    }

    if (diff < 1440) {
      return `${Math.floor(diff / 60)}h ago`;
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };
  // const NotificationItem = React.memo(({ noti, index }) => {
  //     return (
  //         <div
  //             key={`${noti.NotificationId}-${index}`}
  //             onClick={() => {
  //                 setNotificationRow(noti);
  //                 setShowNotificationModal(true);
  //                 if (!noti?.IsMarked) handleMarkRead(noti?.NotificationId);
  //             }}
  //             style={{
  //                 margin: "8px 12px",
  //                 padding: "16px",
  //                 borderRadius: "12px",
  //                 background: getBackgroundColor(noti), // ✅ extra color based on status
  //                 border: "1px solid #e5e7eb",
  //                 boxShadow: noti.IsMarked
  //                     ? "0 4px 12px rgba(15,23,42,0.05)"
  //                     : "0 6px 18px rgba(15,23,42,0.08)",
  //                 cursor: "pointer",
  //                 position: "relative",
  //                 transition: "all 0.25s ease",
  //             }}
  //             onMouseEnter={(e) => {
  //                 e.currentTarget.style.transform = "translateY(-2px)";
  //                 e.currentTarget.style.boxShadow = "0 10px 28px rgba(15,23,42,0.12)";
  //             }}
  //             onMouseLeave={(e) => {
  //                 e.currentTarget.style.transform = "translateY(0)";
  //                 e.currentTarget.style.boxShadow = noti.IsMarked
  //                     ? "0 4px 12px rgba(15,23,42,0.05)"
  //                     : "0 6px 18px rgba(15,23,42,0.08)";
  //             }}>
  //             {/* Left status bar */}
  //             <div
  //                 style={{
  //                     position: "absolute",
  //                     left: 0,
  //                     top: 0,
  //                     bottom: 0,
  //                     width: "4px",
  //                     background: noti.IsMarked ? "#cbd5e1" : getAccentColor(noti),
  //                     borderRadius: "4px 0 0 4px",
  //                 }}
  //             />

  //             <div style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
  //                 {/* Icon */}
  //                 <div
  //                     style={{
  //                         width: "42px",
  //                         height: "42px",
  //                         borderRadius: "10px",
  //                         background: getIconBackground(noti.ReferanceSubType),
  //                         display: "flex",
  //                         alignItems: "center",
  //                         justifyContent: "center",
  //                         flexShrink: 0,
  //                         opacity: noti.IsMarked ? 0.5 : 1,
  //                     }}>
  //                     {getNotificationIcon(noti.ReferanceType, noti.ReferanceSubType)}
  //                 </div>

  //                 {/* Content */}
  //                 <div style={{ flex: 1 }}>
  //                     {/* Header */}
  //                     <div
  //                         style={{
  //                             display: "flex",
  //                             justifyContent: "space-between",
  //                             alignItems: "center",
  //                             marginBottom: "6px",
  //                         }}>
  //                         <div
  //                             style={{
  //                                 fontSize: "11px",
  //                                 fontWeight: noti.IsMarked ? 400 : 600,
  //                                 color:
  //                                     noti.ReferanceSubType?.toLowerCase() === "delete"
  //                                         ? "#dc2626"
  //                                         : "#6b7280",
  //                             }}>
  //                             {noti.ReferanceType}
  //                             {noti.ReferanceSubType?.toLowerCase() === "delete" && (
  //                                 <span style={{ marginLeft: 8, fontWeight: 400 }}>
  //                                     (Deleted)
  //                                 </span>
  //                             )}
  //                         </div>

  //                         <div
  //                             style={{
  //                                 fontSize: "12px",
  //                                 color: "#64748b",
  //                                 whiteSpace: "nowrap",
  //                             }}>
  //                             {formatTimeDisplay(noti.AddedOn)}
  //                         </div>
  //                     </div>

  //                     {/* Message */}
  //                     <div
  //                         style={{
  //                             fontSize: "14px",
  //                             fontWeight: noti.IsMarked ? 400 : 600,
  //                             color: "#0f172a",
  //                             lineHeight: "1.45",
  //                             display: "-webkit-box",
  //                             WebkitLineClamp: 2,
  //                             WebkitBoxOrient: "vertical",
  //                             overflow: "hidden",
  //                         }}>
  //                         {noti.Message}
  //                     </div>
  //                 </div>
  //             </div>
  //         </div>
  //     );
  // });
  const NotificationItem = React.memo(({ noti, index }) => {
    const history = useHistory();
    const [expanded, setExpanded] = useState(false);

    const routeMapping = {
      "General Payment Voucher": "/account/vouchers/payment-entry/general-payment",
      "Purchase Payment Voucher": "/account/vouchers/payment-entry/purchase-payment",
      "Purchase Payment": "/account/vouchers/payment-entry/purchase-payment",
      "Client Receipt Voucher": "/account/vouchers/receipt-entry/client-receipt",
      "Client Receipt": "/account/vouchers/receipt-entry/client-receipt",
      "General Receipt Voucher": "/account/vouchers/receipt-entry/general-receipt",
      "Reserved Voucher": "/account/vouchers/refund-voucher",
      "Journal Voucher": "/account/vouchers/journal-entry",
      "Expense Payment Voucher": "/account/vouchers/expense-voucher",
      "Commission Payment Voucher": "/account/vouchers/payment-entry/com-payment",
      "Loan Payment Voucher": "/account/vouchers/payment-entry/invest-payment",
      "Land Payment Voucher": "/account/vouchers/payment-entry/land-payment",
      "Loan Receipt Voucher": "/account/vouchers/receipt-entry/invest-receipt",
      "Unit Receipt Voucher": "/account/vouchers/receipt-entry/unit-receipt",
      "Deal Sold Receipt Voucher": "/account/vouchers/receipt-entry/deal-sold-rec",
      "External Commission Voucher": "/account/vouchers/external-commission/income",
      "Salary Payment Voucher": "/account/vouchers/payment-entry/salary-payment",
      Contract: "/contract/contracts/with-unit",
      "Adjustment Voucher": "/account/vouchers/general-adjustment",
    };

    const canNavigate = routeMapping[noti?.ReferanceType] && noti?.ReferanceSubType !== "Delete";

    const handleNavigate = () => {
      if (!canNavigate) {
        return;
      }

      if (!noti?.IsMarked) {
        handleMarkRead(noti?.NotificationId);
      }
      onClose();
      history.push(routeMapping[noti.ReferanceType], {
        id: noti.ReferanceId,
        highlightKey: Date.now(),
      });
    };

    return (
      <div
        key={`${noti.NotificationId}-${index}`}
        onClick={handleNavigate}
        style={{
          margin: "8px 12px",
          padding: "10px 12px",
          borderRadius: "12px",
          background: noti.IsMarked ? "#f8fafc" : "#ffffff",
          border: noti.IsMarked ? `1px solid ${primaryTint}` : `1px solid ${primarySoft}`,
          boxShadow: noti.IsMarked
            ? "0 1px 3px rgba(15,23,42,0.04)"
            : "0 6px 16px rgba(15,23,42,0.06)",
          cursor: canNavigate ? "pointer" : "default",
          position: "relative",
          transition: "all 0.2s ease",
          overflow: "hidden",
        }}
        onMouseEnter={(e) => {
          if (canNavigate) {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 10px 22px rgba(15,23,42,0.08)";
            e.currentTarget.style.borderColor = primaryColor;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.border = noti.IsMarked
            ? `1px solid ${primaryTint}`
            : `1px solid ${primarySoft}`;
          e.currentTarget.style.boxShadow = noti.IsMarked
            ? "0 1px 3px rgba(15,23,42,0.04)"
            : "0 6px 16px rgba(15,23,42,0.06)";
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: noti.IsMarked ? primaryTint : primaryColor,
            opacity: noti.IsMarked ? 0.6 : 0.95,
          }}
        />

        <div style={{ display: "flex", gap: "10px" }}>
          <div
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "12px",
              background: getIconBackground(noti.ReferanceSubType),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              opacity: noti.IsMarked ? 0.55 : 0.95,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.04)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            {getNotificationIcon(noti.ReferanceType, noti.ReferanceSubType)}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "4px",
                gap: "6px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: noti.IsMarked ? primaryDark : primaryColor,
                    background: noti.IsMarked ? primaryTint : "#f1f5f9",
                    padding: "3px 8px",
                    borderRadius: "6px",
                    textTransform: "none",
                    letterSpacing: "0.2px",
                    whiteSpace: "nowrap",
                    opacity: noti.IsMarked ? 0.85 : 1,
                  }}
                >
                  {noti.ReferanceType?.split(" ")[0]}
                </div>

                {noti.ReferanceSubType?.toLowerCase() === "delete" && (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: "#991b1b",
                      background: "#fef2f2",
                      padding: "3px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    DELETED
                  </div>
                )}
              </div>

              <div
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  background: noti.IsMarked ? "transparent" : "#f8fafc",
                  padding: "2px 8px",
                  borderRadius: "4px",
                }}
              >
                {formatTimeDisplay(noti.AddedOn)}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "6px",
                fontSize: "12px",
              }}
            >
              <div
                style={{
                  color: "#64748b",
                  fontWeight: 600,
                  background: primaryTint,
                  padding: "2px 8px",
                  borderRadius: "5px",
                  opacity: noti.IsMarked ? 0.6 : 1,
                }}
              >
                ID:{" "}
                <span
                  style={{
                    fontFamily: "monospace",
                    color: primaryColor,
                  }}
                >
                  {noti?.ReferanceId || "—"}
                </span>
              </div>
            </div>

            <div
              style={{
                fontWeight: noti.IsMarked ? 400 : 500,
                color: noti.ReferanceSubType === "Delete" ? "#991b1b" : "#1f2937",
                lineHeight: "1.5",
                display: expanded ? "block" : "-webkit-box",
                WebkitLineClamp: expanded ? "unset" : 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                opacity: noti.IsMarked ? 0.78 : 1,
                marginBottom: "2px",
              }}
            >
              {noti.Message}
            </div>

            {noti.Message?.length > 100 && (
              <div style={{ marginTop: "2px" }}>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => !prev);
                  }}
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: primaryColor,
                    cursor: "pointer",
                    display: "inline-block",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    background: primaryTint,
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = primarySoft;
                    e.currentTarget.style.transform = "translateX(1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = primaryTint;
                    e.currentTarget.style.transform = "translateX(0)";
                  }}
                >
                  {expanded ? "← Show less" : "Show more →"}
                </span>
              </div>
            )}
          </div>

          {!noti.IsMarked && (
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                background: primaryColor,
                flexShrink: 0,
                marginTop: "4px",
                boxShadow: `0 0 0 3px ${primaryColor}22`,
              }}
            />
          )}
        </div>
      </div>
    );
  });

  // const NotificationModal = ({ notificationRow, setShowNotificationModal }) => {
  //     const history = useHistory();

  //     const routeMapping = {
  //         "General Payment Voucher": "/account/vouchers/payment-entry/general-payment",
  //         "General Receipt Voucher": "/account/vouchers/receipt-entry/general-receipt",
  //         "Reserved Voucher": "/account/vouchers/refund-voucher",
  //         "Journal Voucher": "/account/vouchers/journal-entry",
  //         "Expense Payment Voucher": "/account/vouchers/expense-voucher",
  //         "Commission Payment Voucher": "/account/vouchers/payment-entry/com-payment",
  //         "Loan Payment Voucher": "/account/vouchers/payment-entry/invest-payment",
  //         "Land Payment Voucher": "/account/vouchers/payment-entry/land-payment",
  //         "Loan Receipt Voucher": "/account/vouchers/receipt-entry/invest-receipt",
  //         "Unit Receipt Voucher": "/account/vouchers/receipt-entry/unit-receipt",
  //         "Deal Sold Receipt Voucher": "/account/vouchers/receipt-entry/deal-sold-rec",
  //         "External Commission Voucher": "/account/vouchers/external-commission/income",
  //         "Salary Payment Voucher": "/account/vouchers/payment-entry/salary-payment",
  //         Contract: "/contract/contracts/with-unit",
  //         "Adjustment Voucher": "/account/vouchers/general-adjustment",
  //     };

  //     const canNavigate = Boolean(
  //         notificationRow?.ReferanceType &&
  //             routeMapping[notificationRow.ReferanceType] &&
  //             notificationRow?.ReferanceSubType !== "Delete"
  //     );
  //     const handleNavigate = () => {
  //         if (!canNavigate) return;

  //         history.push(routeMapping[notificationRow.ReferanceType], {
  //             id: notificationRow.ReferanceId,
  //             highlightKey: Date.now(),
  //         });
  //         onClose();
  //         setShowNotificationModal(false);
  //     };

  //     const idLabel = notificationRow?.ReferanceType?.toLowerCase().includes("voucher")
  //         ? "Voucher ID"
  //         : notificationRow?.ReferanceType?.toLowerCase().includes("contract")
  //         ? "Contract ID"
  //         : "ID";

  //     return (
  //         <div
  //             id="mymodal"
  //             className="modal"
  //             style={{ display: "block", zIndex: 1100 }}>
  //             <div
  //                 className="modal-content-cat_voucher"
  //                 style={{
  //                     maxWidth: "600px",
  //                     minHeight: "280px",
  //                     maxHeight: "50vh",
  //                     overflow: "hidden",
  //                 }}>
  //                 <div className="row">
  //                     <div className="col-12 page_heading">
  //                         <h4 className="text-center pt-2">Notification Details</h4>
  //                         <span
  //                             className="close pr-2"
  //                             onClick={() => setShowNotificationModal(false)}
  //                             style={{ cursor: "pointer" }}>
  //                             &times;
  //                         </span>
  //                     </div>
  //                 </div>

  //                 {/* Content */}
  //                 <div className="row mt-3">
  //                     <div className="col-6 py-2">
  //                         <b>Type: </b>
  //                         <span>{notificationRow?.ReferanceType}</span>
  //                     </div>

  //                     <div className="col-6 py-2">
  //                         <b>Date: </b>
  //                         <span>
  //                             {moment(notificationRow?.AddedOn)?.format("DD-MMM-YY")}
  //                         </span>
  //                     </div>

  //                     <div className="col-12 py-2">
  //                         <b>{idLabel}: </b>
  //                         <span>{notificationRow?.ReferanceId}</span>
  //                     </div>

  //                     <div className="col-12 py-2">
  //                         <b>Message: </b>
  //                         <p
  //                             className="mt-1"
  //                             style={{
  //                                 wordBreak: "break-word",
  //                                 fontSize: "14px",
  //                                 maxHeight: "150px",
  //                                 overflowY: "auto",
  //                                 paddingRight: "6px",
  //                             }}>
  //                             {notificationRow?.Message}
  //                         </p>
  //                     </div>
  //                 </div>

  //                 {/* Footer */}
  //                 {canNavigate && (
  //                     <div className="row">
  //                         <div className="col-12 d-flex justify-content-end">
  //                             <ActionButton
  //                                 text={`Go to ${notificationRow?.ReferanceType}`}
  //                                 onClick={handleNavigate}
  //                                 newBtn={true}
  //                             />
  //                         </div>
  //                     </div>
  //                 )}
  //             </div>
  //         </div>
  //     );
  // };

  return (
    <>
      <div
        id="myDrawer"
        style={{
          position: "fixed",
          top: 0,
          right: isOpen ? "0" : "-400px",
          width: "420px",
          height: "100vh",
          backgroundColor: "rgba(248, 250, 252, 0.96)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow: isOpen ? "-16px 0 32px rgba(15, 23, 42, 0.12)" : "none",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          transition: "transform 0.3s ease, right 0.3s ease",
          overflow: "hidden",
          borderLeft: isOpen ? "1px solid rgba(148, 163, 184, 0.2)" : "none",
          transform: isOpen ? "translateX(0)" : "translateX(420px)",
          visibility: isOpen ? "visible" : "hidden",
        }}
      >
        <div
          style={{
            padding: "16px 18px 12px",
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
            color: "#ffffff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255, 255, 255, 0.08)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <AiOutlineBell size={22} />
                <div style={{ fontSize: "18px", fontWeight: 700 }}>
                  Notifications ({unreadCount})
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={markAllLoading}
                    style={{
                      background: "rgba(255, 255, 255, 0.12)",
                      color: "white",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      cursor: markAllLoading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      opacity: markAllLoading ? 0.7 : 1,
                    }}
                  >
                    {markAllLoading ? (
                      <ThreeDots color="white" height={14} width={14} />
                    ) : (
                      <>
                        <AiOutlineCheck size={14} />
                        Mark all read
                      </>
                    )}
                  </button>
                )}
                <AiOutlineCloseCircle
                  onClick={onClose}
                  size={22}
                  style={{ cursor: "pointer", opacity: 0.9 }}
                />
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "8px 0 10px",
            background: "#f8fafc",
          }}
        >
          {notificationLoading && currentPage === 1 ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <ThreeDots color={primaryColor} height={40} width={40} />
              <div style={{ marginTop: "10px", color: "#64748b" }}>Loading notifications...</div>
            </div>
          ) : accumulatedNotifications.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "100px 30px",
                color: "#94a3b8",
              }}
            >
              <div
                style={{
                  fontSize: "60px",
                  marginBottom: "20px",
                  opacity: 0.6,
                }}
              >
                🔔
              </div>
              <div style={{ fontSize: "17px", fontWeight: 600 }}>All caught up!</div>
              <div style={{ fontSize: "13px", marginTop: "8px" }}>No notifications to display</div>
            </div>
          ) : (
            <>
              {accumulatedNotifications.map((noti, i) => (
                <NotificationItem key={`${noti.NotificationId}-${i}`} noti={noti} index={i} />
              ))}

              {currentPage < totalPages && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "6px 10px 10px",
                  }}
                >
                  <button
                    onClick={loadMoreNotifications}
                    disabled={loadingMore || notificationLoading}
                    style={{
                      background: "transparent",
                      color: primaryColor,
                      border: `1px solid ${primaryColor}`,
                      padding: "6px 12px",
                      borderRadius: "4px",
                      cursor: loadingMore || notificationLoading ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      fontWeight: 500,
                      opacity: loadingMore || notificationLoading ? 0.7 : 1,
                    }}
                  >
                    {loadingMore || notificationLoading ? "Loading..." : "Load older"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* {showNotificationModal && (
                <NotificationModal
                    notificationRow={notificationRow}
                    setShowNotificationModal={setShowNotificationModal}
                />
            )} */}
    </>
  );
};

export default NotificationDrawer;
